import classNames from 'classnames'
import * as React from 'react'

import {useQueryParams, useQueryParamState} from '../../../hooks/routes'
import {useSlice} from '../../../hooks/useSlice'
import type {Branch} from '../../../services/rest'
import {BuildPageTabNamesEnum, stringifyId} from '../../../types'
import type {BuildId, BuildTypeId, AgentTypeId, ProjectId, TabId, AgentId} from '../../../types'
import {base_uri} from '../../../types/BS_types'
import {stringifyBranch, getBranchParam} from '../../../utils/branchNames'
import isVisualTest from '../../../utils/isVisualTest'
import {objectToQuery, queryToObject} from '../../../utils/queryParams'
import type {QueryParams} from '../../../utils/queryParams'
import {parseURL} from '../../../utils/url'
import {buildParametersTabParam} from '../../pages/BuildPage/BuildParametersTab/BuildParameters.constants'
import {BuildParametersKindTab} from '../../pages/BuildPage/BuildParametersTab/BuildParameters.types'
import IframeAdapter from '../IframeAdapter/IframeAdapter'
import Loader from '../Loader/Loader'

import styles from './ClassicUITabAdapter.css'

export type TabAdapterProps = {
  agentId?: AgentId
  buildTypeId?: BuildTypeId
  buildId?: BuildId
  branch?: Branch | null | undefined
  agentTypeId?: AgentTypeId | null | undefined
  projectId?: ProjectId | null | undefined
  agents?: boolean
  tabId: TabId
  kind?: string | null | undefined
  queued?: boolean
  title: string

  onURLChange?: (url: string) => boolean
}

type BuildFiltersProps = {
  fromBuildNumber?: string | null | undefined
  toBuildNumber?: string | null | undefined
}

interface BuildParametersProps {
  statistics?: boolean
}

const addParamsToInnerUrl = (location: string, params: QueryParams) => {
  const url = parseURL(location)
  url.search = objectToQuery({...queryToObject(url.search), ...params})
  return url.href
}

const getCanonicalIframeUrl = ({
  agentId,
  buildTypeId,
  buildId,
  agentTypeId,
  projectId,
  agents,
  branch,
  tabId,
  kind,
  queued,
  fromBuildNumber,
  toBuildNumber,
  statistics,
}: TabAdapterProps & BuildFiltersProps & BuildParametersProps) => {
  if (
    (!agentId && !agentTypeId && !buildTypeId && !projectId && !buildId && !agents) ||
    (!tabId && !agentTypeId)
  ) {
    return null
  }

  let path = ''
  const params = new URLSearchParams()
  params.set('tab', stringifyId(tabId))

  if (kind != null) {
    params.set('kind', kind)
  }

  if (agentId) {
    params.set('id', stringifyId(agentId))
    path = `${base_uri}/agentDetails.html`
  }

  if (agents === true) {
    path = `${base_uri}/agents.html`
  }

  if (branch) {
    params.set(getBranchParam(projectId), stringifyBranch(branch))
  }

  if (fromBuildNumber) {
    params.set('from', fromBuildNumber)
  }

  if (toBuildNumber) {
    params.set('to', toBuildNumber)
  }

  if (buildId) {
    if (queued === true) {
      params.set('itemId', stringifyId(buildId))
      path = `${base_uri}/viewQueued.html`
    } else {
      params.set('buildId', stringifyId(buildId))
      path = `${base_uri}/viewLog.html`
    }

    if (tabId === BuildPageTabNamesEnum.buildParameters) {
      const onlyModified = new URLSearchParams(location.search).get('onlyModified')
      if (onlyModified) {
        params.set('onlyModified', onlyModified)
      }
      if (statistics) {
        params.set(buildParametersTabParam, BuildParametersKindTab.STATISTICS)
        params.set('hideTabs', 'true')
      }
    }
  }

  if (agentTypeId) {
    params.set('agentTypeId', stringifyId(agentTypeId))
    path = `${base_uri}/agentDetails.html`
  }

  if (projectId) {
    params.set('projectId', stringifyId(projectId))
    path = `${base_uri}/project.html`
  }

  if (buildTypeId) {
    params.set('buildTypeId', stringifyId(buildTypeId))
    path = `${base_uri}/viewType.html`
  }

  return addParamsToInnerUrl(`${path}?${params.toString()}`, {
    embedded: 'true',
  })
}

function ClassicUITabAdapter(props: TabAdapterProps & BuildParametersProps) {
  const {tabId, onURLChange} = props
  const {from: fromBuildNumber, to: toBuildNumber} = useQueryParams()

  const [src, setSrc] = useQueryParamState(
    'iframeSrc',
    getCanonicalIframeUrl({...props, fromBuildNumber, toBuildNumber}),
  )
  const [ready, {load, unload}] = useSlice(
    {
      load: (_, __: void) => true,
      unload: (_, __: void) => false,
    },
    false,
  )
  const handleURLChange = React.useCallback(
    (newURL: string) => {
      const prevent = onURLChange?.(newURL)
      if (!prevent) {
        setSrc(
          addParamsToInnerUrl(newURL, {
            embedded: 'true',
          }),
        )
      }
    },
    [setSrc, onURLChange],
  )

  if (src == null) {
    return null
  }

  const showLoader = !ready || isVisualTest()
  return (
    <>
      {showLoader && <Loader />}
      {
        <IframeAdapter
          name="Classic UI tab"
          autoHeight={tabId.startsWith('report_')}
          title={props.title}
          className={classNames(styles.iframe, {
            [styles.loading]: showLoader,
          })}
          onIframeLoaded={load}
          onIframeUnload={unload}
          onURLChange={handleURLChange}
          src={src}
          preventFlashing
        />
      }
    </>
  )
}

export default React.memo(ClassicUITabAdapter)
